import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Tag, Input, Select, Space, Dropdown, message, Modal, Switch
} from 'antd';
import {
  PlusOutlined, SearchOutlined, MoreOutlined, EditOutlined,
  DeleteOutlined, ThunderboltOutlined, ExperimentOutlined
} from '@ant-design/icons';
import moment from 'moment';
import EmailAutomationService from '../../services/EmailAutomationService';
import ROUTES from '../../constants/routes';
import { PageContainer, StatsRow, StatCard, StyledTable, PaginationText } from '../Surveys/styled';
import useResponsive from '../../hooks/useResponsive';
import MobileCardList from '../../components/Responsive/MobileCardList';
import ResponsiveFilterBar from '../../components/Responsive/ResponsiveFilterBar';

const STATUS_COLORS = {
  active: 'green',
  paused: 'orange',
  draft: 'default'
};

const EmailAutomationsPage = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [automations, setAutomations] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState();
  const [triggerFilter, setTriggerFilter] = useState();

  useEffect(() => {
    EmailAutomationService.getTriggerEvents().then((res) => {
      if (res.success) setTriggers(res.data?.triggers || []);
    });
  }, []);

  const triggerLabel = useCallback((key) => {
    const t = triggers.find((x) => x.key === key);
    return t?.label || key;
  }, [triggers]);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await EmailAutomationService.listAutomations({
      page: pagination.current,
      limit: pagination.pageSize,
      search,
      status: statusFilter,
      triggerEvent: triggerFilter
    });
    if (result.success) {
      setAutomations(result.data.automations || []);
      setPagination((p) => ({ ...p, total: result.data.pagination?.total || 0 }));
    } else {
      message.error(result.error);
    }
    setLoading(false);
  }, [pagination.current, pagination.pageSize, search, statusFilter, triggerFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = (automation) => {
    Modal.confirm({
      title: `Delete automation "${automation.name}"?`,
      content: 'This will permanently delete the automation. New events will no longer trigger this email.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        const result = await EmailAutomationService.deleteAutomation(automation._id);
        if (result.success) {
          message.success('Automation deleted');
          load();
        } else {
          message.error(result.error);
        }
      }
    });
  };

  const handleToggle = async (automation, checked) => {
    const result = await EmailAutomationService.toggleStatus(
      automation._id,
      checked ? 'active' : 'paused'
    );
    if (result.success) {
      message.success(checked ? 'Automation activated' : 'Automation paused');
      load();
    } else {
      message.error(result.error);
    }
  };

  const handleTestFire = (automation) => {
    let testEmail = '';
    Modal.confirm({
      title: `Test "${automation.name}"`,
      content: (
        <div>
          <p style={{ marginTop: 0 }}>Send a sample email using mock variables to verify rendering.</p>
          <Input
            placeholder="Override recipient (optional)"
            onChange={(e) => { testEmail = e.target.value; }}
          />
        </div>
      ),
      okText: 'Send test',
      onOk: async () => {
        const result = await EmailAutomationService.testFire(automation._id, testEmail ? { testEmail } : {});
        if (result.success) message.success('Test email sent');
        else message.error(result.error);
      }
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name, a) => (
        <a onClick={() => navigate(`/admin/email-automations/${a._id}/edit`)}>
          <strong>{name}</strong>
          {a.description ? (
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{a.description}</div>
          ) : null}
        </a>
      )
    },
    {
      title: 'Trigger',
      dataIndex: 'triggerEvent',
      width: 220,
      render: (key) => <Tag color="purple">{triggerLabel(key)}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (status, a) => (
        <Space>
          <Switch
            size="small"
            checked={status === 'active'}
            onChange={(checked) => handleToggle(a, checked)}
          />
          <Tag color={STATUS_COLORS[status]}>{status?.toUpperCase()}</Tag>
        </Space>
      )
    },
    { title: 'Triggered', dataIndex: ['stats', 'triggered'], width: 100, render: (n) => n || 0 },
    { title: 'Sent', dataIndex: ['stats', 'sent'], width: 80, render: (n) => n || 0 },
    { title: 'Failed', dataIndex: ['stats', 'failed'], width: 80, render: (n) => n || 0 },
    {
      title: 'Last Triggered',
      dataIndex: 'lastTriggeredAt',
      width: 160,
      render: (d) => d ? moment(d).fromNow() : '—'
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, a) => (
        <Dropdown
          menu={{
            items: [
              { key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: () => navigate(`/admin/email-automations/${a._id}/edit`) },
              { key: 'test', icon: <ExperimentOutlined />, label: 'Send test', onClick: () => handleTestFire(a) },
              { type: 'divider' },
              { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true, onClick: () => handleDelete(a) }
            ]
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      )
    }
  ];

  const totalSent = automations.reduce((sum, a) => sum + (a.stats?.sent || 0), 0);
  const activeCount = automations.filter((a) => a.status === 'active').length;

  return (
    <PageContainer>
      <StatsRow>
        <StatCard><div className="label">Total Automations</div><div className="value">{pagination.total}</div></StatCard>
        <StatCard><div className="label">Active</div><div className="value">{activeCount}</div></StatCard>
        <StatCard><div className="label">Emails Sent (page)</div><div className="value">{totalSent}</div></StatCard>
      </StatsRow>

      <ResponsiveFilterBar
        activeFilterCount={[statusFilter, triggerFilter].filter(Boolean).length}
        search={(
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search automations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280, height: 40 }}
            allowClear
          />
        )}
        filters={[
          <Select
            key="status"
            placeholder="Status"
            style={{ width: 160, height: 40 }}
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: 'Active', value: 'active' },
              { label: 'Paused', value: 'paused' },
              { label: 'Draft', value: 'draft' }
            ]}
          />,
          <Select
            key="trigger"
            placeholder="Trigger"
            style={{ width: 240, height: 40 }}
            allowClear
            value={triggerFilter}
            onChange={setTriggerFilter}
            options={triggers.map((t) => ({ label: t.label, value: t.key }))}
          />
        ]}
        primaryAction={{
          label: 'New Automation',
          icon: <PlusOutlined />,
          onClick: () => navigate(ROUTES.ADMIN_EMAIL_AUTOMATION_BUILDER_NEW),
          style: { background: '#1a1a1a', borderColor: '#1a1a1a' }
        }}
      />

      {isMobile ? (
        <MobileCardList
          records={automations}
          rowKey="_id"
          loading={loading}
          onRowClick={(a) => navigate(`/admin/email-automations/${a._id}/edit`)}
          emptyText="No automations yet"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize }))
          }}
          renderCard={(a) => (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{a.name}</div>
                  {a.description && (
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{a.description}</div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                  <Switch
                    size="small"
                    checked={a.status === 'active'}
                    onChange={(checked) => handleToggle(a, checked)}
                  />
                  <Tag color={STATUS_COLORS[a.status]}>{a.status?.toUpperCase()}</Tag>
                </div>
              </div>
              <div style={{ marginTop: 8 }}>
                <Tag color="purple">{triggerLabel(a.triggerEvent)}</Tag>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8,
                marginTop: 12,
                fontSize: 12
              }}>
                <div><div style={{ color: '#8c8c8c' }}>Triggered</div><div style={{ fontWeight: 600 }}>{a.stats?.triggered || 0}</div></div>
                <div><div style={{ color: '#8c8c8c' }}>Sent</div><div style={{ fontWeight: 600 }}>{a.stats?.sent || 0}</div></div>
                <div><div style={{ color: '#8c8c8c' }}>Failed</div><div style={{ fontWeight: 600 }}>{a.stats?.failed || 0}</div></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {a.lastTriggeredAt ? `Last: ${moment(a.lastTriggeredAt).fromNow()}` : 'Never triggered'}
                </span>
                <Dropdown
                  menu={{
                    items: [
                      { key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: () => navigate(`/admin/email-automations/${a._id}/edit`) },
                      { key: 'test', icon: <ExperimentOutlined />, label: 'Send test', onClick: () => handleTestFire(a) },
                      { type: 'divider' },
                      { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true, onClick: () => handleDelete(a) }
                    ]
                  }}
                  trigger={['click']}
                >
                  <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                </Dropdown>
              </div>
            </div>
          )}
        />
      ) : (
        <StyledTable
          columns={columns}
          dataSource={automations}
          rowKey="_id"
          loading={loading}
          size="middle"
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <ThunderboltOutlined style={{ fontSize: 36, color: '#bfbfbf' }} />
                <div style={{ marginTop: 12, color: '#8c8c8c' }}>
                  No automations yet. Create one to send emails when platform events happen.
                </div>
              </div>
            )
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => (
              <PaginationText>Showing {range[0]}-{range[1]} of {total} automations</PaginationText>
            ),
            onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize }))
          }}
        />
      )}
    </PageContainer>
  );
};

export default EmailAutomationsPage;
